import { useEffect, useState } from "react";

/**
 * Resolves the embedded StrikeUp widget's `displayType` once the widget
 * has finished its server-side config handshake. Returns `null` while the
 * widget is loading or if the widget script never became available.
 *
 * Used by AskStrikeUpButton to decide whether to render itself — the button is
 * only meaningful in the custom-trigger modes (`custom-trigger` and
 * `custom-trigger-panel`), where nothing else opens the pane. Test with
 * isCustomTriggerDisplay() rather than comparing to a single literal.
 */
export function useStrikeUpChat(): {
  displayType: string | null;
  isReady: boolean;
} {
  const [displayType, setDisplayType] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    // The widget script loads asynchronously — poll briefly until it's there,
    // then subscribe to its ready event.
    const attach = () => {
      const api = window.StrikeUpChat;
      if (!api) return false;
      unsubscribe = api.onReady((resolvedDisplayType) => {
        if (cancelled) return;
        setDisplayType(resolvedDisplayType);
        setIsReady(true);
      });
      return true;
    };

    if (!attach()) {
      const interval = window.setInterval(() => {
        if (attach()) window.clearInterval(interval);
      }, 100);
      // Stop polling after 10s; the widget probably isn't going to load.
      const timeout = window.setTimeout(
        () => window.clearInterval(interval),
        10_000,
      );
      return () => {
        cancelled = true;
        window.clearInterval(interval);
        window.clearTimeout(timeout);
        unsubscribe?.();
      };
    }

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  return { displayType, isReady };
}
